<template>
  <div class="dish-search-results">
    <!-- Shimmer loading state -->
    <div v-if="isLoading && dishes.length === 0" class="shimmer-container">
      <div v-for="i in 6" :key="i" class="shimmer-card">
        <div class="shimmer-image"></div>
        <div class="shimmer-content">
          <div class="shimmer-title"></div>
          <div class="shimmer-text"></div>
          <div class="shimmer-price"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!isLoading && dishes.length === 0" class="empty-state">
      <div class="empty-icon">🍽️</div>
      <h3 class="empty-title">Aucun plat trouvé</h3>
      <p class="empty-message">
        Essayez d'ajuster votre recherche ou vos filtres pour trouver ce que
        vous cherchez.
      </p>
    </div>

    <!-- Dish results list -->
    <div
      v-else
      class="dishes-list"
      @scroll="handleScroll"
      ref="scrollContainer"
    >
      <div class="results-header" v-if="dishes.length > 0">
        <span class="results-count">{{ dishes.length }} plats trouvés</span>
      </div>

      <div
        v-for="dish in dishes"
        :key="dish.id"
        class="dish-card"
        @click="handleDishClick(dish)"
      >
        <ImgComponent
          :src="
            dish.principalImageUrl ||
            dish.imageUrl ||
            dish.image ||
            '/images/default-dish.jpg'
          "
          :alt="dish.title || dish.name"
          class="dish-image"
          loading="lazy"
        />

        <div class="dish-content">
          <h4 class="dish-title flex items-center gap-2">
            {{ dish.title || dish.name }}
            <span
              v-if="dish.promotion"
              class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary text-white shadow-sm"
            >
              <template v-if="dish.promotion.discountType === 'percentage'">
                -{{ dish.promotion.discount }}%
              </template>
              <template v-else-if="dish.promotion.discountType === 'fixed'">
                -{{ dish.promotion.discount }} F
              </template>
            </span>
          </h4>

          <p class="dish-description">
            {{ dish.description || 'Aucune description disponible' }}
          </p>

          <div class="dish-meta">
            <div class="flex items-end gap-1">
              <span class="dish-price" :class="dish.promotion ? 'text-lg underline' : ''">
                {{ formatPrice(dish.promotion ? dish.promotion.promotionalPrice : dish.price) }}
              </span>
              <span v-if="dish.promotion" class="text-xs text-slate-400 line-through font-medium mb-[2px]">
                {{ formatPrice(dish.price) }}
              </span>
            </div>

            <span v-if="dish.restaurant" class="dish-restaurant">
              {{ dish.restaurant?.name?.name  || dish.restaurant?.name  }}
            </span>
          </div>

          <div v-if="dish.tags && dish.tags.length > 0" class="dish-tags">
            <span v-for="tag in dish.tags.slice(0, 3)" :key="tag" class="tag">
              {{ tag }}
            </span>
          </div>
        </div>

        <div class="flex flex-col justify-end items-end pb-1">
          <div
            @click.stop="handleAddToCart(dish)"
            class="ripple z-[2] text-2xl rounded-full min-w-8 w-8 h-8 p-1 flex items-center justify-center transition-all duration-300"
            :class="{
              'text-white bg-primary shadow-lg shadow-primary/30': isAddedToCart(dish.id) && dish.canOrder,
              'text-primary bg-primary-50 border border-primary/20': !isAddedToCart(dish.id) && dish.canOrder,
              'text-gray-400 bg-gray-100 cursor-not-allowed': !dish.canOrder
            }"
          >
            <ColorImg
              v-if="!isAddedToCart(dish.id)"
              src="/images/all/add-to-cart.svg"
              class="bg-primary w-full h-full"
              alt=""
            />
            <ImgComponent
              v-else
              src="/images/all/check-white.svg"
              class="w-full h-full p-0.5"
              alt=""
            />
          </div>
        </div>
      </div>

      <!-- Loading more indicator -->
      <div v-if="isLoading && dishes.length > 0" class="loading-more">
        <div class="spinner"></div>
        <span>Chargement...</span>
      </div>

      <!-- End of results -->
      <div v-if="!hasMore && dishes.length > 0" class="end-message">
        Fin des résultats
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  watch,
  ref
} from 'vue';
// Import controller to ensure it's loaded
import '../controller';

export default {
  name: 'DishSearchResultList',

  props: {
    searchQuery: {
      type: String,
      default: ''
    },

    restaurantId: {
      type: [String, Number],
      default: null
    },

    filters: {
      type: Object,
      default: () => ({})
    },

    autoSearch: {
      type: Boolean,
      default: true
    }
  },

  setup(props) {
    const { proxy } = getCurrentInstance();
    const { $stores, $bus, $constants } = proxy;

    // Store current filters from search-filters feature
    const currentFilters = ref(null);

    // Debounce timeout for search queries
    let searchDebounceTimeout = null;
    const DEBOUNCE_DELAY = 300; // 300ms debounce

    // Prevent infinite search loops
    const isSearching = ref(false);
    const lastSearchQuery = ref(null);
    const lastSearchFilters = ref(null);

    // Computed properties from store
    const dishes = computed(() => {
      return $stores.dishSearch?.state?.dishes || [];
    });

    const isLoading = computed(() => {
      return $stores.dishSearch?.state?.loading || false;
    });

    const hasMore = computed(() => {
      return $stores.dishSearch?.state?.hasMore || false;
    });


    // Helper function to get price range min/max
    const getPriceRangeBounds = (priceRangeId) => {
      // PRICE_RANGES is exported from search-filters/constants.js and merged into $constants
      const priceRanges = $constants?.PRICE_RANGES || [];
      const range = priceRanges.find((p) => p.id === priceRangeId);
      if (range) {
        return { min: range.min, max: range.max };
      }
      return null;
    };

    // Methods
    const performSearch = () => {
      if (!$bus || !$constants) {
        console.error(
          '[DishSearchResultList] Cannot perform search - $bus or $constants not available'
        );
        return;
      }

      // Prevent infinite loops
      const currentQuery = props.searchQuery || '';
      const currentFiltersString = JSON.stringify({
        ...props.filters,
        restaurantId: props.restaurantId,
        currentFilters: currentFilters.value
      });

      if (
        isSearching.value &&
        lastSearchQuery.value === currentQuery &&
        lastSearchFilters.value === currentFiltersString
      ) {
        console.log('[DishSearchResultList] Skipping duplicate search');
        return;
      }

      // Update last search state
      lastSearchQuery.value = currentQuery;
      lastSearchFilters.value = currentFiltersString;
      isSearching.value = true;

      // Build filters from currentFilters (from search-filters feature) or props
      let searchFilters = {
        ...props.filters,
        restaurantId: props.restaurantId
      };

      // Apply filters from search-filters feature if available
      if (currentFilters.value) {
        const { sort, priceRange, categories } = currentFilters.value;

        // Add categoryId from selectedCategories (single category for now)
        if (categories && categories.length > 0) {
          searchFilters.categoryId = categories[0];
        }

        // Add sort parameter
        if (sort) {
          searchFilters.sort = sort;
        }

        // Add price range bounds
        if (priceRange) {
          const bounds = getPriceRangeBounds(priceRange);
          if (bounds) {
            searchFilters.minPrice = bounds.min;
            searchFilters.maxPrice = bounds.max;
          }
        }
      }

      console.log('[DishSearchResultList] Performing search with filters:', {
        searchQuery: props.searchQuery,
        filters: searchFilters,
        currentFilters: currentFilters.value
      });

      $bus.emit($constants.EVENTS.DISH_SEARCH.REQUEST, {
        searchQuery: props.searchQuery,
        filters: searchFilters,
        page: 1
      });

      // Reset searching flag after a short delay
      setTimeout(() => {
        isSearching.value = false;
      }, 100);
    };

    const handleScroll = (event) => {
      const container = event.target;
      const scrollBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollBottom < 100 && hasMore.value && !isLoading.value) {
        $bus?.emit($constants.EVENTS.DISH_SEARCH.LOAD_MORE);
      }
    };

    const handleDishClick = (dish) => {
      showDetails(dish)
    };

    const handleAddToCart = (dish) => {
      addToCart(dish)
    };


    function showDetails(dish) {
      if (!dish || !dish?.id) return;
      $bus.emit('dish.SHOW_DISH_DETAIL', {
        id: dish.id
      });
    }
    const cartItems = computed(() => $stores?.cart?.state?.cart?.items || []);
    const isAddedToCart = (dishId) => {
      return cartItems.value.some((item) => item.dishId === dishId);
    };

    const getCartItemId = (dishId) => {
      return cartItems.value.find((item) => item.dishId === dishId)?.id || null;
    };

    function addToCart(dish) {
      if (!dish || !dish.id) {
        console.error('Invalid item for adding to cart');
        return;
      }

      // Check for removal toggle
      if (isAddedToCart(dish.id)) {
        const cartId = getCartItemId(dish.id);
        if (cartId) {
          $bus.emit($constants.EVENTS.CART.ITEMS.REMOVE, {
            itemId: cartId
          });
          return;
        }
      }

      // Check if dish can be ordered using API-enriched data
      console.log("🚀 ~ addToCart ~ dish.canOrder:", dish.canOrder)
      // if (!dish.canOrder) {
      //   if (!dish.isRestaurantOpen) {
      //     $bus.emit($constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      //       type: 'warning',
      //       title: 'Restaurant fermé',
      //       body: 'Ce restaurant est actuellement fermé. Vous ne pouvez pas passer de commande.'
      //     });
      //   } else {
      //     $bus.emit($constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      //       type: 'warning',
      //       title: 'Plat indisponible',
      //       body: 'Ce plat est actuellement indisponible à la commande.'
      //     });
      //   }
      //   return;
      // }

      // Emit cart add event to trigger API call and store update
      $bus.emit($constants.EVENTS.CART.ITEMS.ADD, {
        restaurantId: dish.storeId,
        restaurantName:
          dish.restaurant?.name || dish.storeName || 'Restaurant',
        dishId: dish.id,
        title: dish.title,
        name: dish.title,
        price: dish.promotion ? dish.promotion.promotionalPrice : dish.price,
        originalPrice: dish.promotion ? dish.price : undefined,
        promotion: dish.promotion || undefined,
        quantity: 1,
        currency: dish.currency || 'XOF',
        description: dish.description,
        category: dish.category,
        categoryId: dish.categoryId,
        principalImageUrl: dish.principalImageUrl || dish.image,
        options: [],
        drinks: [],
        fullOptions: dish.options,
        fullAccompaniments: dish.accompaniments
      });
    }

    const formatPrice = (price) => {
      if (!price) return '0 FCFA';
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF'
      }).format(price);
    };

    // Handle filters updated event from search-filters feature
    const handleFiltersUpdated = (filters) => {
      console.log(
        '[DishSearchResultList] Filters updated event received:',
        filters
      );

      // Store the current filters
      currentFilters.value = filters;

      // Re-trigger search if autoSearch is enabled
      if (props.autoSearch) {
        // Clear existing debounce timeout
        if (searchDebounceTimeout) {
          clearTimeout(searchDebounceTimeout);
        }

        // Debounce the search to avoid excessive API calls
        searchDebounceTimeout = setTimeout(() => {
          performSearch();
        }, DEBOUNCE_DELAY);
      }
    };

    // Watch for changes
    watch(
      () => props.searchQuery,
      () => {
        if (props.autoSearch) {
          // Clear existing debounce timeout
          if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
          }

          // Debounce the search to avoid excessive API calls
          searchDebounceTimeout = setTimeout(() => {
            performSearch();
          }, DEBOUNCE_DELAY);
        }
      }
    );

    watch(
      () => props.filters,
      (newFilters) => {
        if (props.autoSearch) {
          // Clear existing debounce timeout
          if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
          }

          // Debounce the search
          searchDebounceTimeout = setTimeout(() => {
            performSearch();
          }, DEBOUNCE_DELAY);
        }
      },
      { deep: true }
    );

    watch(
      () => props.restaurantId,
      () => {
        if (props.autoSearch) {
          performSearch();
        }
      }
    );

    // Lifecycle
    onMounted(() => {
      console.log('[DishSearchResultList] Component mounted');

      // Load initial filters from store if available
      const searchFiltersStore = $stores?.searchFilters;
      if (searchFiltersStore?.state?.filters) {
        currentFilters.value = searchFiltersStore.state.filters;
        console.log(
          '[DishSearchResultList] Loaded initial filters from store:',
          currentFilters.value
        );
      }

      // Listen for filter updates
      if (!$bus || !$constants) {
        console.error(
          '[DishSearchResultList] $bus or $constants not available'
        );
        return;
      }

      $bus.on(
        $constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED,
        handleFiltersUpdated
      );
      console.log('[DishSearchResultList] Event listeners registered');

      // Initial search if autoSearch is enabled
      if (props.autoSearch) {
        performSearch();
      }
    });

    onBeforeUnmount(() => {
      console.log('[DishSearchResultList] Component unmounting');

      // Clean up debounce timeout
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }

      // Clean up event listener
      if (!$bus || !$constants) {
        console.warn(
          '[DishSearchResultList] $bus or $constants not available during cleanup'
        );
        return;
      }

      $bus.off(
        $constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED,
        handleFiltersUpdated
      );
      console.log('[DishSearchResultList] Event listeners cleaned up');
    });

    return {
      dishes,
      isLoading,
      hasMore,
      performSearch,
      handleScroll,
      handleDishClick,
      handleAddToCart,
      formatPrice,
      isAddedToCart
    };
  }
};
</script>

<style scoped>
.dish-search-results {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* Shimmer Loading State */
.shimmer-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shimmer-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 12px;
}

.shimmer-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.shimmer-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shimmer-title {
  width: 60%;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.shimmer-text {
  width: 80%;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.shimmer-price {
  width: 30%;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.empty-message {
  font-size: 14px;
  color: #666;
  max-width: 300px;
}

/* Dishes List */
.dishes-list {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.results-header {
  padding: 8px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.results-count {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

/* Dish Card */
.dish-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  border: 1px solid #e0e0e0;
}

.dish-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dish-image {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.dish-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.dish-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dish-description {
  font-size: 13px;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dish-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: auto;
}

.dish-price {
  font-size: 16px;
  font-weight: 700;
  color: #4caf50;
}

.dish-restaurant {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dish-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.tag {
  font-size: 11px;
  padding: 2px 8px;
  background: #f0f0f0;
  color: #666;
  border-radius: 12px;
}



/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: #666;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* End Message */
.end-message {
  text-align: center;
  padding: 24px;
  color: #999;
  font-size: 14px;
}
</style>
